import { apiRequest } from './client.js';

/**
 * Vendor Profile — one screen over vendor facts that already existed but were
 * spread across several screens.
 *
 * Branding types and geography are NOT served from here: the screen calls the
 * endpoints that already own them — vendorBrandingProfileApi
 * (/v1/m17/vendor-branding-profiles) and orgApi's geo-scope helpers
 * (/v1/geo-scope) — so each fact keeps one origin and one API.
 */

export interface VendorListItem {
  readonly vendorId: string;
  readonly vendorCode: string;
  readonly vendorName: string;
  readonly status: string;
}

export interface VendorGeneral {
  readonly vendorId: string;
  readonly vendorCode: string;
  readonly vendorName: string;
  readonly legalName: string;
  readonly contactPerson: string;
  readonly mobileNumber: string;
  readonly email: string | null;
  /** Street line only; the place is the ids below. */
  readonly address: string | null;
  readonly addressStateId: string | null;
  readonly addressDistrictId: string | null;
  readonly addressTehsilId: string | null;
  readonly addressCityId: string | null;
  readonly addressStateName: string | null;
  readonly addressDistrictName: string | null;
  readonly addressTehsilName: string | null;
  readonly addressCityName: string | null;
  /** Stored on the vendor itself; the village directory cannot supply it for
   *  an urban address. */
  readonly addressPincode: string | null;
  readonly status: string;
  readonly activeSince: string;
  readonly validTo: string;
  readonly lastModifiedOn: string;
  readonly loginId: string | null;
}

export interface VendorBank {
  readonly accountNumber: string | null;
  readonly ifscCode: string | null;
  readonly accountHolderName: string | null;
  readonly bankName: string | null;
  readonly branchName: string | null;
  readonly accountTypeCode: string | null;
}

export interface VendorGst {
  readonly gstNumber: string | null;
  readonly panNumber: string | null;
}

export interface VendorProfile {
  readonly general: VendorGeneral;
  readonly bank: VendorBank;
  readonly gst: VendorGst;
}

export interface SaveOutcome {
  readonly changed: boolean;
}

const BASE = '/v1/m17/vendor-profile';

export async function listProfileVendors(token: string | null): Promise<VendorListItem[]> {
  const r = await apiRequest<{ data: VendorListItem[] }>(`${BASE}/vendors`, { token });
  return r.data;
}

export async function getVendorProfile(
  vendorId: string,
  token: string | null,
): Promise<VendorProfile> {
  const r = await apiRequest<{ data: VendorProfile }>(`${BASE}/${vendorId}`, { token });
  return r.data;
}

/** One branding order released to the vendor (read-only). */
export interface ReleasedOrder {
  readonly requestLineId: string;
  readonly lineNumber: number | null;
  readonly requestNo: string | null;
  readonly dealerName: string | null;
  readonly dealerCode: string | null;
  readonly typeName: string | null;
  readonly itemName: string | null;
  readonly quantity: string | number | null;
  readonly uomCode: string | null;
  readonly woNumber: string | null;
  readonly status: string | null;
  readonly forwardedAt: string | null;
  /** True for work needing no dealer: the vendor completes it himself. */
  readonly completedByVendor: boolean;
  /** The latest completion of the line, when the vendor has reported one. */
  readonly completionId: string | null;
  readonly completionStatus: 'DRAFT' | 'SUBMITTED' | 'APPROVED' | 'REJECTED' | null;
}

/** GET :vendorId/released-orders — the branding orders released to this vendor. */
export async function listReleasedOrders(
  vendorId: string,
  token: string | null,
): Promise<readonly ReleasedOrder[]> {
  const r = await apiRequest<{ data: ReleasedOrder[] }>(
    `${BASE}/${vendorId}/released-orders`,
    { token },
  );
  return r.data;
}

/**
 * GET /v1/vendor/branding-orders — the same released-order rows, for the vendor
 * signed in. The backend resolves the vendor from the login, never the client.
 */
export async function listMyReleasedOrders(
  token: string | null,
): Promise<readonly ReleasedOrder[]> {
  const r = await apiRequest<{ items: ReleasedOrder[] }>(
    '/v1/vendor/branding-orders',
    { token },
  );
  if (!Array.isArray(r.items)) {
    throw new Error('Released branding orders response did not contain an items list.');
  }
  return r.items;
}

export interface PincodeMatch {
  readonly cityId: string;
  readonly cityName: string;
  readonly tehsilId: string;
  readonly tehsilName: string;
  readonly districtId: string;
  readonly districtName: string;
  readonly stateId: string;
  readonly stateName: string;
  readonly pincode: string;
}

export async function lookupPincode(
  pincode: string,
  token: string | null,
): Promise<PincodeMatch[]> {
  const r = await apiRequest<{ data: PincodeMatch[] }>(
    `${BASE}/pincode/${encodeURIComponent(pincode)}`, { token },
  );
  return r.data;
}

async function put(
  vendorId: string,
  segment: string,
  body: Record<string, unknown>,
  token: string | null,
): Promise<SaveOutcome> {
  const r = await apiRequest<{ data: SaveOutcome }>(`${BASE}/${vendorId}/${segment}`, {
    method: 'PUT', token, body,
  });
  return r.data;
}

export const saveVendorGeneral = (
  vendorId: string, body: Record<string, unknown>, token: string | null,
) => put(vendorId, 'general', body, token);

export const saveVendorBank = (
  vendorId: string, body: Record<string, unknown>, token: string | null,
) => put(vendorId, 'bank', body, token);

export const saveVendorGst = (
  vendorId: string, body: Record<string, unknown>, token: string | null,
) => put(vendorId, 'gst', body, token);
